import React, { useMemo, useState } from "react";
import { ArrowRight, Check, Sparkles, Eye, Route, AlertTriangle, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────
   MarketingHealthCheck · Phase 5 Homepage
   ------------------------------------------------------------
   A DARK section combining:
     • Pipeline Health Check teaser + inline 60-second diagnostic
     • Final CTA (same dark canvas — no separate dark block)

   Frontend-only marketing tool:
     • No API calls
     • No lead-capture gate — the score is shown BEFORE any
       contact request
     • No stored personal information
     • No predictive claims (fall-through %, £ at risk, exchange
       probability) — the diagnostic is operational, not actuarial

   Score is a deterministic, explainable weighted map of the
   answers.  Range is clamped to 0–100.
   ───────────────────────────────────────────────────────────── */

const DIMENSIONS = ["visibility", "progression", "risk", "consistency"];

/* Question set — 7 concise questions.  Every option carries a
   0-100 contribution to each of the four dimensions.  The final
   dimension score is the average of its contributing options.
   The overall score is the weighted mean of the four dimensions. */
const QUESTIONS = [
  {
    id: "q1",
    prompt: "How many live sales are you progressing right now?",
    hint: "Sales agreed and pre-exchange.",
    input: "number",
    min: 0,
    max: 500,
    default: 15,
    /* Volume factor is used as a multiplier on complexity — high
       volume with low tooling → lower consistency contribution. */
  },
  {
    id: "q2",
    prompt: "How many people actively manage sales progression?",
    input: "number",
    min: 0,
    max: 200,
    default: 2,
  },
  {
    id: "q3",
    prompt: "How does your team currently track progression?",
    input: "choice",
    options: [
      { id: "crm",         label: "CRM",                          v: { visibility: 45, progression: 40, risk: 30, consistency: 45 } },
      { id: "sheet",       label: "Spreadsheet",                  v: { visibility: 40, progression: 30, risk: 25, consistency: 30 } },
      { id: "dedicated",   label: "Dedicated progression system", v: { visibility: 85, progression: 85, risk: 75, consistency: 80 } },
      { id: "combination", label: "A combination of systems",     v: { visibility: 50, progression: 45, risk: 40, consistency: 40 } },
    ],
  },
  {
    id: "q4",
    prompt: "How often is the pipeline formally reviewed?",
    input: "choice",
    options: [
      { id: "daily",   label: "Daily",                   v: { visibility: 90, progression: 80, risk: 75, consistency: 80 } },
      { id: "weekly",  label: "Weekly",                  v: { visibility: 75, progression: 70, risk: 65, consistency: 70 } },
      { id: "adhoc",   label: "Ad hoc / when needed",    v: { visibility: 45, progression: 40, risk: 35, consistency: 35 } },
      { id: "rarely",  label: "Rarely",                  v: { visibility: 25, progression: 20, risk: 20, consistency: 25 } },
    ],
  },
  {
    id: "q5",
    prompt: "How quickly can you identify which deals are blocked?",
    input: "choice",
    options: [
      { id: "instant", label: "Instantly — one screen",  v: { visibility: 95, progression: 85, risk: 90, consistency: 80 } },
      { id: "minutes", label: "In a few minutes",        v: { visibility: 75, progression: 70, risk: 70, consistency: 70 } },
      { id: "hours",   label: "It takes some digging",   v: { visibility: 45, progression: 40, risk: 40, consistency: 45 } },
      { id: "days",    label: "Often only when chased",  v: { visibility: 25, progression: 25, risk: 20, consistency: 30 } },
    ],
  },
  {
    id: "q6",
    prompt: "Does every important next action have a clear owner?",
    input: "choice",
    options: [
      { id: "always",    label: "Always",           v: { visibility: 85, progression: 90, risk: 80, consistency: 90 } },
      { id: "usually",   label: "Usually",          v: { visibility: 70, progression: 70, risk: 65, consistency: 70 } },
      { id: "sometimes", label: "Sometimes",        v: { visibility: 45, progression: 45, risk: 45, consistency: 45 } },
      { id: "rarely",    label: "Rarely",           v: { visibility: 25, progression: 25, risk: 30, consistency: 25 } },
    ],
  },
  {
    id: "q7",
    prompt: "Do target exchange dates often move because issues are identified late?",
    input: "choice",
    options: [
      { id: "no",         label: "Rarely",              v: { visibility: 85, progression: 85, risk: 90, consistency: 80 } },
      { id: "sometimes",  label: "Sometimes",           v: { visibility: 60, progression: 55, risk: 55, consistency: 55 } },
      { id: "often",      label: "Often",               v: { visibility: 35, progression: 30, risk: 30, consistency: 35 } },
      { id: "always",     label: "Almost every deal",   v: { visibility: 20, progression: 20, risk: 20, consistency: 20 } },
    ],
  },
];

const DIM_META = {
  visibility:   { label: "Visibility",           Icon: Eye },
  progression:  { label: "Progression control",  Icon: Route },
  risk:         { label: "Risk detection",       Icon: AlertTriangle },
  consistency:  { label: "Process consistency",  Icon: Layers },
};

const clamp = (n, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, n));

/* Deterministic score — average of the option contributions
   selected across the four dimensions.  Only questions Q3–Q7
   contribute directly to dimensions; Q1/Q2 lightly modulate
   Process consistency to reflect operational scale. */
const scoreAnswers = (answers) => {
  const sums = { visibility: [], progression: [], risk: [], consistency: [] };
  for (const q of QUESTIONS) {
    if (q.input !== "choice") continue;
    const optId = answers[q.id];
    const opt = q.options.find((o) => o.id === optId);
    if (!opt) continue;
    for (const d of DIMENSIONS) sums[d].push(opt.v[d]);
  }
  const dims = {};
  for (const d of DIMENSIONS) {
    const arr = sums[d];
    dims[d] = arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 50;
  }
  /* Scale modifier: many deals per progression head hurts consistency. */
  const liveSales = Number(answers.q1) || 0;
  const heads = Math.max(1, Number(answers.q2) || 0);
  const ratio = liveSales / heads;
  if (ratio > 30) dims.consistency = clamp(dims.consistency - 10);
  else if (ratio > 15) dims.consistency = clamp(dims.consistency - 5);
  const overall = Math.round((dims.visibility + dims.progression + dims.risk + dims.consistency) / 4);
  return { overall, ...dims };
};

const scoreBand = (n) => {
  if (n >= 85) return "Excellent — strong operational control";
  if (n >= 70) return "Good — areas to strengthen";
  if (n >= 55) return "Fair — meaningful improvements available";
  return "Needs attention — foundation work required";
};

const strongest = (scores) => {
  const arr = DIMENSIONS.map((d) => ({ d, v: scores[d] }));
  arr.sort((a, b) => b.v - a.v);
  return DIM_META[arr[0].d].label;
};

const weakest = (scores) => {
  const arr = DIMENSIONS.map((d) => ({ d, v: scores[d] }));
  arr.sort((a, b) => a.v - b.v);
  return { key: arr[0].d, label: DIM_META[arr[0].d].label };
};

const OPPORTUNITY_MAP = {
  visibility:  "Bring every transaction into a single live view",
  progression: "Give every deal a clear next action and owner",
  risk:        "Earlier blocker detection",
  consistency: "One consistent progression process across the team",
};

const RECOMMENDATIONS_MAP = {
  visibility: [
    "Consolidate transaction data into one screen the whole team can see.",
    "Add a daily portfolio review focused on deals needing attention.",
    "Make blockers visible without opening individual deals.",
  ],
  progression: [
    "Assign a single owner to every next action.",
    "Move progression steps to a shared, dated timeline per deal.",
    "Replace ad-hoc email chases with structured tasks.",
  ],
  risk: [
    "Flag deals that have been waiting on a party for more than 3 days.",
    "Surface upcoming exchange dates against outstanding blockers.",
    "Log every response so response times are always visible.",
  ],
  consistency: [
    "Standardise the progression stages every deal moves through.",
    "Document the checklist each deal must clear to reach exchange.",
    "Give every progression owner the same operating view.",
  ],
};

const CHAINFLOW_HELP_MAP = {
  visibility:  "The Flow Centre gives the whole team one live view of every active sale.",
  progression: "Deal Rooms hold every action, owner and outcome — nothing lives in email.",
  risk:        "Priority signals surface deals slipping before target exchange dates.",
  consistency: "Every deal follows the same progression backbone across the agency.",
};

/* ─── Example (illustrative) result for the teaser panel ─── */
const EXAMPLE = {
  overall: 72,
  visibility: 82,
  progression: 74,
  risk: 58,
  consistency: 67,
  opportunity: "Earlier blocker detection",
};

/* ─── UI ─────────────────────────────────────────────────── */

const DarkPill = ({ children }) => (
  <span
    className="inline-flex items-center gap-1 h-5 px-2 rounded-md text-[10px] font-bold uppercase tracking-[0.16em]"
    style={{ background: "rgba(167,139,250,0.14)", color: "#C4B5FD", border: "1px solid rgba(167,139,250,0.24)" }}
  >
    {children}
  </span>
);

const ScoreRing = ({ value, size = 132, stroke = 8 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={r}
          fill="none" stroke="url(#hc-grad)" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`}
        />
        <defs>
          <linearGradient id="hc-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#A5B4FC" />
            <stop offset="100%" stopColor="#C4B5FD" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-[34px] font-bold text-white tabular-nums leading-none">{value}</span>
        <span className="text-[10px] font-semibold text-slate-400 mt-1 tabular-nums">/ 100</span>
      </div>
    </div>
  );
};

const DimensionRow = ({ dim, value, dark = false }) => {
  const { label, Icon } = DIM_META[dim];
  return (
    <div className={`flex items-center gap-3 py-2 ${dark ? "" : "text-slate-800"}`}>
      <span
        className="w-7 h-7 rounded-lg inline-flex items-center justify-center shrink-0"
        style={{
          background: dark ? "rgba(167,139,250,0.14)" : "#F5F3FF",
          color: dark ? "#C4B5FD" : "#6D28D9",
          border: dark ? "1px solid rgba(167,139,250,0.24)" : "1px solid #E9D5FF",
        }}
      >
        <Icon size={13} strokeWidth={2.2} />
      </span>
      <div className="flex-1 min-w-0">
        <p className={`text-[11.5px] font-semibold ${dark ? "text-slate-200" : "text-slate-700"} leading-tight`}>{label}</p>
        <div className={`mt-1.5 h-1.5 rounded-full overflow-hidden ${dark ? "bg-white/10" : "bg-slate-100"}`}>
          <div
            className="h-full rounded-full"
            style={{
              width: `${value}%`,
              background: "linear-gradient(90deg, #6366F1 0%, #A855F7 100%)",
            }}
          />
        </div>
      </div>
      <span className={`text-[13px] font-bold tabular-nums ${dark ? "text-white" : "text-slate-900"} w-8 text-right`}>
        {value}
      </span>
    </div>
  );
};

const TeaserCard = ({ onStart }) => (
  <div
    className="rounded-2xl p-6 lg:p-7"
    style={{
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.10)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 40px 80px -32px rgba(0,0,0,0.55)",
    }}
    data-testid="landing-healthcheck-teaser"
  >
    <div className="flex items-center gap-2 mb-4">
      <DarkPill>Example assessment</DarkPill>
      <span className="text-[10.5px] font-semibold text-slate-400">Illustrative only</span>
    </div>
    <div className="flex items-center gap-5">
      <ScoreRing value={EXAMPLE.overall} />
      <div className="min-w-0">
        <p className="font-display text-[20px] font-bold text-white leading-tight">
          {scoreBand(EXAMPLE.overall)}
        </p>
        <p className="text-[12px] text-slate-400 mt-2">
          Biggest opportunity:
          <span className="text-slate-100 font-semibold ml-1.5">{EXAMPLE.opportunity}</span>
        </p>
      </div>
    </div>

    <div className="mt-6 space-y-1">
      <DimensionRow dim="visibility"   value={EXAMPLE.visibility}   dark />
      <DimensionRow dim="progression"  value={EXAMPLE.progression}  dark />
      <DimensionRow dim="risk"         value={EXAMPLE.risk}         dark />
      <DimensionRow dim="consistency"  value={EXAMPLE.consistency}  dark />
    </div>

    <button
      type="button"
      onClick={onStart}
      className="mt-6 w-full h-12 rounded-xl text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-[1px]"
      style={{
        background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
        boxShadow: "0 18px 36px -12px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
      }}
      data-testid="landing-healthcheck-start"
    >
      Start the 60-second check
      <ArrowRight size={14} strokeWidth={2.4} />
    </button>
  </div>
);

const QuestionCard = ({ q, value, onChange }) => (
  <div className="rounded-2xl p-5 lg:p-6 bg-white ring-1 ring-slate-200/70" data-testid={`hc-question-${q.id}`}>
    <p className="text-[10px] font-bold uppercase tracking-[0.20em] text-violet-700">Question {q.id.slice(1)} / {QUESTIONS.length}</p>
    <p className="font-display text-[17px] lg:text-[19px] font-bold text-slate-900 leading-snug mt-2">{q.prompt}</p>
    {q.hint && <p className="text-[12px] text-slate-500 mt-1.5">{q.hint}</p>}

    {q.input === "number" && (
      <input
        type="number"
        min={q.min}
        max={q.max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-4 w-full h-12 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none text-[15px] font-semibold text-slate-900 tabular-nums"
        placeholder={String(q.default)}
        data-testid={`hc-input-${q.id}`}
        aria-label={q.prompt}
      />
    )}

    {q.input === "choice" && (
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {q.options.map((o) => {
          const on = value === o.id;
          return (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => onChange(o.id)}
                aria-pressed={on}
                className={`w-full h-12 px-4 rounded-xl inline-flex items-center gap-2 text-left text-[13px] font-semibold transition ${
                  on
                    ? "text-white"
                    : "text-slate-800 bg-white ring-1 ring-slate-200 hover:ring-slate-300"
                }`}
                style={
                  on
                    ? { background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)" }
                    : undefined
                }
                data-testid={`hc-opt-${q.id}-${o.id}`}
              >
                <span
                  className={`w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 ${
                    on ? "bg-white/20" : "ring-1 ring-slate-300"
                  }`}
                >
                  {on && <Check size={12} strokeWidth={3} className="text-white" />}
                </span>
                <span className="min-w-0 truncate">{o.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const ResultView = ({ scores, onRetake }) => {
  const nav = useNavigate();
  const w = weakest(scores);
  const strong = strongest(scores);
  const opportunity = OPPORTUNITY_MAP[w.key];
  const recs = RECOMMENDATIONS_MAP[w.key];
  const help = CHAINFLOW_HELP_MAP[w.key];
  return (
    <div className="rounded-2xl bg-white ring-1 ring-slate-200/70 p-6 lg:p-8" data-testid="hc-result">
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-md text-[10.5px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-200">
          <Sparkles size={11} strokeWidth={2.4} />
          Your result
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[auto_minmax(0,1fr)] gap-6 items-center">
        <div
          className="rounded-2xl p-5"
          style={{
            background: "linear-gradient(180deg, #0B1130 0%, #080B22 100%)",
          }}
        >
          <ScoreRing value={scores.overall} />
        </div>
        <div className="min-w-0">
          <p className="font-display text-[22px] font-bold text-slate-900 leading-tight" data-testid="hc-result-band">
            {scoreBand(scores.overall)}
          </p>
          <p className="text-[12.5px] text-slate-600 mt-2">
            Strongest area: <span className="font-semibold text-slate-900">{strong}</span>
          </p>
          <p className="text-[12.5px] text-slate-600 mt-1">
            Biggest opportunity: <span className="font-semibold text-slate-900">{opportunity}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-6">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.20em] text-slate-500">Dimension breakdown</p>
          <div className="mt-3 space-y-1">
            <DimensionRow dim="visibility"   value={scores.visibility} />
            <DimensionRow dim="progression"  value={scores.progression} />
            <DimensionRow dim="risk"         value={scores.risk} />
            <DimensionRow dim="consistency"  value={scores.consistency} />
          </div>
        </div>
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.20em] text-slate-500">Three actions to take</p>
          <ol className="mt-3 space-y-2.5">
            {recs.map((r, i) => (
              <li key={i} className="flex gap-2.5 text-[13px] text-slate-700 leading-snug">
                <span className="w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 bg-violet-50 text-violet-700 text-[11px] font-bold tabular-nums">
                  {i + 1}
                </span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div
        className="mt-6 rounded-xl px-4 py-3.5"
        style={{ background: "#F5F3FF", border: "1px solid #E9D5FF" }}
      >
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-800">How ChainFlow would help</p>
        <p className="text-[13px] text-slate-700 leading-snug mt-1.5">{help}</p>
      </div>

      <p className="text-[11px] text-slate-400 mt-5 leading-relaxed">
        Your score is based on the answers provided and ChainFlow's assessment framework.
        It is intended as an operational diagnostic, not a prediction of transaction outcomes.
      </p>

      <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <button
          type="button"
          onClick={() => nav("/signup")}
          className="h-11 px-5 rounded-xl text-white text-[13px] font-bold inline-flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)" }}
          data-testid="hc-result-request-access"
        >
          See how ChainFlow could improve this
          <ArrowRight size={13} strokeWidth={2.4} />
        </button>
        <button
          type="button"
          onClick={onRetake}
          className="h-11 px-4 rounded-xl text-[13px] font-semibold text-slate-700 hover:bg-slate-100"
          data-testid="hc-result-retake"
        >
          Retake the check
        </button>
      </div>
    </div>
  );
};

const FinalCTAPanel = () => {
  const nav = useNavigate();
  return (
    <div className="mt-16 lg:mt-24 max-w-[880px] mx-auto text-center" data-testid="landing-final-cta">
      <h3 className="font-display text-[28px] sm:text-[36px] lg:text-[44px] font-bold text-white tracking-[-0.028em] leading-[1.06]">
        See ChainFlow using a<br />
        <span
          style={{
            background: "linear-gradient(90deg, #A5B4FC 0%, #C4B5FD 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          realistic agency pipeline.
        </span>
      </h3>
      <p className="text-[14px] sm:text-[15.5px] text-slate-300 leading-relaxed mt-4 max-w-[560px] mx-auto">
        A focused walkthrough of how ChainFlow brings transaction visibility,
        priorities and progression into one place.
      </p>
      <button
        type="button"
        onClick={() => nav("/signup")}
        className="mt-6 h-12 px-6 rounded-xl text-white text-[13.5px] font-bold inline-flex items-center gap-2 transition-transform hover:-translate-y-[1px]"
        style={{
          background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
          boxShadow: "0 18px 36px -12px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
        }}
        data-testid="landing-final-cta-request-access"
      >
        Request access
        <ArrowRight size={14} strokeWidth={2.4} />
      </button>
    </div>
  );
};

export const MarketingHealthCheck = () => {
  const [mode, setMode] = useState("teaser"); /* teaser | questions | result */
  const initial = useMemo(() => ({
    q1: QUESTIONS[0].default,
    q2: QUESTIONS[1].default,
    q3: null, q4: null, q5: null, q6: null, q7: null,
  }), []);
  const [answers, setAnswers] = useState(initial);

  const allChoicesAnswered = QUESTIONS
    .filter((q) => q.input === "choice")
    .every((q) => !!answers[q.id]);

  const scores = useMemo(() => scoreAnswers(answers), [answers]);

  return (
    <section
      id="pipeline-health-check"
      className="relative overflow-hidden"
      data-testid="landing-healthcheck"
      style={{
        background:
          "radial-gradient(1100px 700px at 10% 0%, rgba(124,58,237,0.24), transparent 60%)," +
          "radial-gradient(900px 700px at 90% 110%, rgba(79,107,255,0.20), transparent 55%)," +
          "linear-gradient(180deg, #0B1130 0%, #0A0E28 55%, #080B22 100%)",
      }}
    >
      <span
        aria-hidden="true"
        className="absolute -top-16 left-[-160px] w-[760px] h-[560px] rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.20), transparent 70%)" }}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.28) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.28) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 30%, transparent 75%)",
        }}
      />

      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-24">
        {mode === "teaser" && (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-8 lg:gap-14 items-center">
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-300">
                Free pipeline health check
              </p>
              <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[52px] font-bold tracking-[-0.028em] text-white leading-[1.02] mt-4">
                How healthy is your<br />
                <span
                  style={{
                    background: "linear-gradient(90deg, #A5B4FC 0%, #C4B5FD 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  sales pipeline?
                </span>
              </h2>
              <p className="text-[14px] sm:text-[15.5px] text-slate-300 leading-relaxed mt-5 max-w-[520px]">
                Answer a few questions and get an instant view of your pipeline's
                visibility, risk and progression health.
              </p>
              <button
                type="button"
                onClick={() => setMode("questions")}
                className="mt-6 h-12 px-6 rounded-xl text-white text-[13.5px] font-bold inline-flex items-center gap-2 transition-transform hover:-translate-y-[1px]"
                style={{
                  background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
                  boxShadow: "0 18px 36px -12px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
                }}
                data-testid="landing-healthcheck-start-primary"
              >
                Start the 60-second check
                <ArrowRight size={14} strokeWidth={2.4} />
              </button>
            </div>
            <TeaserCard onStart={() => setMode("questions")} />
          </div>
        )}

        {mode === "questions" && (
          <div className="max-w-[820px] mx-auto">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-300 text-center">
              60-second pipeline health check
            </p>
            <h2 className="font-display text-[24px] sm:text-[30px] font-bold text-white text-center leading-tight mt-3">
              Quick diagnostic — no signup required.
            </h2>
            <p className="text-[12.5px] text-slate-400 text-center mt-2">
              Your answers stay in your browser.
            </p>

            <div className="mt-8 space-y-4">
              {QUESTIONS.map((q) => (
                <QuestionCard
                  key={q.id}
                  q={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                />
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
              <button
                type="button"
                disabled={!allChoicesAnswered}
                onClick={() => setMode("result")}
                className="h-12 px-6 rounded-xl text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)" }}
                data-testid="hc-see-result"
              >
                See my score
                <ArrowRight size={14} strokeWidth={2.4} />
              </button>
              <button
                type="button"
                onClick={() => setMode("teaser")}
                className="h-12 px-4 rounded-xl text-[13px] font-semibold text-slate-200 hover:bg-white/5"
                data-testid="hc-cancel"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {mode === "result" && (
          <div className="max-w-[880px] mx-auto">
            <ResultView scores={scores} onRetake={() => { setAnswers(initial); setMode("questions"); }} />
          </div>
        )}

        <FinalCTAPanel />
      </div>
    </section>
  );
};

export default MarketingHealthCheck;
